import { clamp, fmt } from '../util/math.js';

let hud = null;
let ghostHealth = 1;
const killFeed = [];

const $ = (id) => document.getElementById(id);

export function initHud() {
  hud = {
    root: $('hud'),
    scoreText: $('scoreText'),
    waveText: $('waveText'),
    leftText: $('leftText'),
    streakText: $('streakText'),
    healthText: $('healthText'),
    healthBar: $('healthBar'),
    bossWrap: $('bossWrap'),
    bossHpText: $('bossHpText'),
    bossBar: $('bossBar'),
    weaponHud: $('weaponHud'),
    weaponName: $('weaponName'),
    weaponMeta: $('weaponMeta'),
    weaponKey: $('weaponKey'),
    overdriveBar: $('overdriveBar'),
    overdriveText: $('overdriveText'),
    dashBar: $('dashBar'),
    dashText: $('dashText'),
    mapSizeText: $('mapSizeText'),
    healthGhost: createHealthGhost($('healthBar')),
    killFeed: createKillFeed(),
  };
  injectHudStyles();
  return hud;
}

export function updateHud(game, options = {}) {
  if (!hud) initHud();

  const enemies = options.enemies || [];
  const spawnQueue = game.spawnQueue || [];
  setText(hud.scoreText, fmt(game.score || 0));
  setText(hud.waveText, String(game.wave || 1));
  setText(hud.leftText, game.waveIntroTimer > 0 ? `IN ${Math.ceil(game.waveIntroTimer)}` : String(enemies.length + spawnQueue.length));
  const combo = game.comboKills || 0;
  setText(hud.streakText, combo > 0 ? `x${combo}` : 'x1');
  const sector = game.mapSector ? ` - ${game.mapSector}` : '';
  setText(hud.mapSizeText, `${Math.round(game.mapSize || 72)}m${sector}`);

  const maxHealth = game.maxHealth || 100;
  const health = clamp(game.health ?? maxHealth, 0, maxHealth);
  const healthPct = clamp(health / maxHealth, 0, 1);
  ghostHealth = Math.max(healthPct, ghostHealth - 0.018);
  setText(hud.healthText, game.mercyTimer > 0 ? `SAFE ${Math.ceil(game.mercyTimer)}s` : `${Math.ceil((health / maxHealth) * 100)}%`);
  if (hud.healthBar) {
    hud.healthBar.style.width = `${healthPct * 100}%`;
    hud.healthBar.parentElement?.classList.toggle('critical-health', healthPct < 0.3);
  }
  if (hud.healthGhost) hud.healthGhost.style.width = `${ghostHealth * 100}%`;
  if (hud.streakText) {
    const mult = clamp(combo || 1, 1, 6);
    hud.streakText.style.fontSize = `${16 + (mult - 1) * 6.4}px`;
    hud.streakText.style.color = mult >= 4 ? '#ffd166' : '';
  }

  const boss = game.boss || options.boss;
  if (boss?.alive) {
    const pct = clamp(boss.hp / boss.maxHp, 0, 1);
    hud.bossWrap?.classList.remove('hidden');
    setText(hud.bossHpText, `${Math.ceil(pct * 100)}%`);
    if (hud.bossBar) hud.bossBar.style.width = `${pct * 100}%`;
  } else {
    hud.bossWrap?.classList.add('hidden');
  }

  updateWeaponHud(options.weapon, game);
  updateKillFeed();
}

export function updateWeaponHud(weapon, game = {}) {
  if (!hud) initHud();
  if (!weapon) return;

  setText(hud.weaponName, weapon.name);
  setText(hud.weaponMeta, weapon.meta);
  setText(hud.weaponKey, weapon.key);

  const overdrive = clamp((game.overdriveCharge || 0) / 100, 0, 1);
  if (hud.overdriveBar) hud.overdriveBar.style.width = `${overdrive * 100}%`;
  setText(hud.overdriveText, overdrive >= 1 ? 'Ready' : `${Math.floor(overdrive * 100)}%`);

  const dashReady = clamp(1 - (game.dashCooldown || 0) / 1.75, 0, 1);
  if (hud.dashBar) hud.dashBar.style.width = `${dashReady * 100}%`;
  setText(hud.dashText, game.dashCooldown <= 0 ? 'Ready' : `${Math.ceil(game.dashCooldown * 10) / 10}s`);
}

function setText(element, text) {
  if (element) element.textContent = text;
}

export function pushKillFeed(weapon, enemy) {
  if (!hud) initHud();
  const item = {
    weapon: weapon?.name || 'Weapon',
    icon: weaponIcon(weapon?.id),
    enemy: enemy?.label || enemy?.type || 'Target',
    ttl: 4,
  };
  killFeed.unshift(item);
  killFeed.length = Math.min(killFeed.length, 5);
  renderKillFeed();
}

function updateKillFeed() {
  if (!hud?.killFeed) return;
  let dirty = false;
  for (const item of killFeed) {
    item.ttl -= 1 / 60;
    if (item.ttl <= 0) dirty = true;
  }
  for (let i = killFeed.length - 1; i >= 0; i -= 1) {
    if (killFeed[i].ttl <= 0) killFeed.splice(i, 1);
  }
  if (dirty) renderKillFeed();
}

function renderKillFeed() {
  if (!hud?.killFeed) return;
  hud.killFeed.replaceChildren(...killFeed.map((item) => {
    const row = document.createElement('div');
    row.className = 'kill-feed-row';
    row.style.opacity = String(clamp(item.ttl, 0, 1));
    row.innerHTML = `<b>${item.icon}</b><span>${escapeText(item.enemy)}</span>`;
    row.title = item.weapon;
    return row;
  }));
}

function createHealthGhost(healthBar) {
  const parent = healthBar?.parentElement;
  if (!parent || parent.querySelector('.health-ghost')) return parent?.querySelector('.health-ghost') || null;
  const ghost = document.createElement('i');
  ghost.className = 'health-ghost';
  parent.insertBefore(ghost, healthBar);
  return ghost;
}

function createKillFeed() {
  let element = document.getElementById('killFeed');
  if (!element) {
    element = document.createElement('div');
    element.id = 'killFeed';
    document.body.appendChild(element);
  }
  return element;
}

function injectHudStyles() {
  if (document.getElementById('phase4HudStyles')) return;
  const style = document.createElement('style');
  style.id = 'phase4HudStyles';
  style.textContent = `
    .health-wrap .bar { position:relative; }
    .health-ghost {
      position:absolute; inset:0 auto 0 0; display:block; height:100%;
      border-radius:inherit; background:rgba(255,255,255,.34);
      transition:width 420ms ease; z-index:0;
    }
    #healthBar { position:relative; z-index:1; background:linear-gradient(90deg,#ff3156,#ffd166); }
    .critical-health { box-shadow:0 0 22px rgba(255,49,86,.58), inset 0 0 12px rgba(255,49,86,.24); }
    .critical-health #healthBar { animation:healthPulse 620ms ease-in-out infinite alternate; }
    @keyframes healthPulse { from { filter:brightness(1); } to { filter:brightness(1.55); } }
    #killFeed {
      pointer-events:none; position:fixed; right:clamp(14px,3vw,28px); bottom:190px; z-index:9;
      display:grid; gap:7px; justify-items:end;
    }
    .kill-feed-row {
      display:flex; align-items:center; gap:9px; min-width:142px; justify-content:flex-start;
      padding:8px 10px; border:1px solid rgba(255,255,255,.14); border-radius:8px;
      background:rgba(4,7,14,.68); color:#eaf4ff; font-size:12px; font-weight:850;
      text-transform:uppercase; letter-spacing:.04em; box-shadow:0 14px 34px rgba(0,0,0,.28);
      backdrop-filter:blur(14px);
    }
    .kill-feed-row b { color:#53ffb5; font-size:15px; line-height:1; }
    body.gamepad-active .controls-help::after {
      content:"Controller: LS move / RS aim / RT fire / LT dash / RB/LB weapons";
      display:block; margin-top:6px; color:#53ffb5;
    }
  `;
  document.head.appendChild(style);
}

function weaponIcon(id) {
  if (id === 'shotgun') return 'S12';
  if (id === 'rail') return 'RL';
  if (id === 'plasma') return 'NV';
  return 'SMG';
}

function escapeText(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char]));
}
